import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Slider,
  Grid,
  Card,
  CardContent,
  Alert,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAmplitude } from '../hooks/useAmplitude';
import { styled } from '@mui/material/styles';

// OpenCV.js types
declare global {
  interface Window {
    cv: {
      Mat: any;
      imread: (element: HTMLImageElement | HTMLCanvasElement) => any;
      cvtColor: (src: any, dst: any, code: number) => void;
      Canny: (src: any, dst: any, threshold1: number, threshold2: number, apertureSize?: number, L2gradient?: boolean) => void;
      GaussianBlur: (src: any, dst: any, ksize: any, sigmaX: number, sigmaY?: number, borderType?: number) => void;
      bitwise_not: (src: any, dst: any) => void;
      bitwise_or: (src1: any, src2: any, dst: any) => void;
      imshow: (canvas: HTMLCanvasElement, mat: any) => void;
      imencode: (ext: string, mat: any) => any;
      Size: new (width: number, height: number) => any;
      onRuntimeInitialized: () => void;
      ready?: Promise<void>;
      COLOR_RGBA2GRAY?: number;
      BORDER_DEFAULT?: number;
      findContours: (image: any, contours: any, hierarchy: any, mode: number, method: number, offset?: any) => void;
      RETR_EXTERNAL?: number;
      RETR_LIST?: number;
      CHAIN_APPROX_SIMPLE?: number;
      CHAIN_APPROX_NONE?: number;
      approxPolyDP: (curve: any, approxCurve: any, epsilon: number, closed: boolean) => void;
      MatVector: new () => any;
    };
  }
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const ImageToSVGConverter: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { track } = useAmplitude();
  const isRTL = currentLang === 'he';

  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [svgOutput, setSvgOutput] = useState<string | null>(null);
  const [cannyLow, setCannyLow] = useState<number>(100); // Canny lower threshold
  const [cannyHigh, setCannyHigh] = useState<number>(120); // Canny upper threshold
  const [blurAmount, setBlurAmount] = useState<number>(3.0); // Gaussian blur sigma
  const [simplifyEpsilon, setSimplifyEpsilon] = useState<number>(0.5); // Contour simplification
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opencvReady, setOpencvReady] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load OpenCV.js (only once globally)
  useEffect(() => {
    // Check if OpenCV is already loaded and ready
    if (window.cv && typeof window.cv.Mat !== 'undefined') {
      setOpencvReady(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="opencv.js"]');
    if (existingScript) {
      // Wait for existing script to finish loading
      const checkReady = setInterval(() => {
        if (window.cv && typeof window.cv.Mat !== 'undefined') {
          setOpencvReady(true);
          clearInterval(checkReady);
        }
      }, 100);
      return () => clearInterval(checkReady);
    }

    // Check if OpenCV is already initialized via onRuntimeInitialized
    if (window.cv && typeof window.cv.onRuntimeInitialized === 'function') {
      // Already has initialization callback, wrap it
      const originalCallback = window.cv.onRuntimeInitialized;
      window.cv.onRuntimeInitialized = () => {
        originalCallback();
        setOpencvReady(true);
      };
      return;
    }

    // Load OpenCV.js from CDN
    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.10.0/opencv.js';
    script.async = true;
    script.id = 'opencv-js-script';
    
    script.onload = () => {
      // Wait for OpenCV to be ready
      if (window.cv && window.cv.ready) {
        window.cv.ready.then(() => {
          setOpencvReady(true);
        });
      } else if (window.cv && typeof window.cv.onRuntimeInitialized === 'function') {
        // Wrap existing callback
        const originalCallback = window.cv.onRuntimeInitialized;
        window.cv.onRuntimeInitialized = () => {
          originalCallback();
          setOpencvReady(true);
        };
      } else {
        // Set up callback for runtime initialization
        window.cv = window.cv || {};
        window.cv.onRuntimeInitialized = () => {
          setOpencvReady(true);
        };
      }
    };
    
    script.onerror = () => {
      setError('Failed to load OpenCV.js library');
    };
    
    document.head.appendChild(script);
    
    // Don't remove script on unmount to avoid conflicts
    // The script will persist and can be reused by other components
  }, []);

  // Load image when file is selected
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check authentication before processing
    if (!isAuthenticated) {
      setError(t('pages.freeTools.imageToSVG.errors.loginRequired'));
      // Optionally redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { state: { from: '/free-tools' } });
      }, 2000);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError(t('pages.freeTools.imageToSVG.errors.invalidFile'));
      return;
    }

    setError(null);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setLoading(false);
        // Track image upload event
        track('Image to SVG - Image Uploaded', {
          file_type: file.type,
          file_size: file.size,
          image_width: img.width,
          image_height: img.height,
        });
        // Process immediately after loading
        setTimeout(() => processImage(img), 100);
      };
      img.onerror = () => {
        setError(t('pages.freeTools.imageToSVG.errors.loadFailed'));
        setLoading(false);
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      setError(t('pages.freeTools.imageToSVG.errors.readFailed'));
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  // Detect and remove background
  const removeBackground = (imageData: ImageData): ImageData => {
    const data = new Uint8ClampedArray(imageData.data);
    const width = imageData.width;
    const height = imageData.height;

    // Strategy: Background is usually at the edges (corners)
    // Collect colors from edges to identify background
    const edgeColors: Array<{ r: number; g: number; b: number }> = [];
    const edgeSize = Math.min(width, height) * 0.1; // Sample 10% of edges

    // Sample corners and edges
    for (let y = 0; y < edgeSize; y++) {
      for (let x = 0; x < edgeSize; x++) {
        // Top-left corner
        const idx1 = (y * width + x) * 4;
        edgeColors.push({ r: data[idx1], g: data[idx1 + 1], b: data[idx1 + 2] });
        
        // Top-right corner
        const idx2 = (y * width + (width - 1 - x)) * 4;
        edgeColors.push({ r: data[idx2], g: data[idx2 + 1], b: data[idx2 + 2] });
        
        // Bottom-left corner
        const idx3 = ((height - 1 - y) * width + x) * 4;
        edgeColors.push({ r: data[idx3], g: data[idx3 + 1], b: data[idx3 + 2] });
        
        // Bottom-right corner
        const idx4 = ((height - 1 - y) * width + (width - 1 - x)) * 4;
        edgeColors.push({ r: data[idx4], g: data[idx4 + 1], b: data[idx4 + 2] });
      }
    }

    // Calculate average background color
    let avgR = 0, avgG = 0, avgB = 0;
    for (const color of edgeColors) {
      avgR += color.r;
      avgG += color.g;
      avgB += color.b;
    }
    avgR = Math.round(avgR / edgeColors.length);
    avgG = Math.round(avgG / edgeColors.length);
    avgB = Math.round(avgB / edgeColors.length);

    // Calculate color distance threshold (similar colors to background)
    const bgTolerance = 30; // Adjustable tolerance for background detection

    // Remove background by making similar pixels transparent/white
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      
      // Calculate distance from background color
      const colorDistance = Math.sqrt(
        Math.pow(r - avgR, 2) + Math.pow(g - avgG, 2) + Math.pow(b - avgB, 2)
      );

      // If similar to background, make it white (will not be detected as edge)
      if (colorDistance < bgTolerance) {
        data[i] = 255; // R
        data[i + 1] = 255; // G
        data[i + 2] = 255; // B
      }
    }

    return new ImageData(data, width, height);
  };

  // Convert to grayscale (simple conversion for Canny input)
  const convertToGrayscale = (imageData: ImageData): ImageData => {
    const data = new Uint8ClampedArray(imageData.data);
    const width = imageData.width;
    const height = imageData.height;

    // Convert to grayscale
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    return new ImageData(data, width, height);
  };


  // Canny edge detection using OpenCV.js with optimal parameters
  const cannyEdgeDetection = (imageData: ImageData, lowThreshold: number, highThreshold: number, blurSigma: number): Promise<ImageData> => {
    if (!opencvReady || !window.cv) {
      throw new Error('OpenCV.js is not loaded yet');
    }

    const cv = window.cv;
    const width = imageData.width;
    const height = imageData.height;

    return new Promise((resolve, reject) => {
      try {
        // Create a temporary canvas and put ImageData on it
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) {
          reject(new Error('Failed to create canvas context'));
          return;
        }
        tempCtx.putImageData(imageData, 0, 0);

        // Convert canvas to data URL, then load as Image
        const dataUrl = tempCanvas.toDataURL();
        const img = new Image();
        
        img.onload = () => {
          try {
            // Read image from Image element using OpenCV
            const src = cv.imread(img);
            
            // Convert to grayscale (Canny works on single channel)
            const gray = new cv.Mat();
            // Use cv.COLOR_RGBA2GRAY constant (value is typically 6)
            const COLOR_RGBA2GRAY = cv.COLOR_RGBA2GRAY !== undefined ? cv.COLOR_RGBA2GRAY : 6;
            cv.cvtColor(src, gray, COLOR_RGBA2GRAY);

            // Apply Gaussian blur to reduce noise (optimal kernel size 5x5)
            const blurred = new cv.Mat();
            const ksize = new cv.Size(5, 5);
            const BORDER_DEFAULT = cv.BORDER_DEFAULT !== undefined ? cv.BORDER_DEFAULT : 4;
            cv.GaussianBlur(gray, blurred, ksize, blurSigma, blurSigma, BORDER_DEFAULT);

            // Apply Canny edge detection with L2 gradient for better accuracy
            // For internal features, we need to detect edges in both directions
            const edges = new cv.Mat();
            cv.Canny(blurred, edges, lowThreshold, highThreshold, 3, true); // Use L2 gradient
            
            // Invert the image and detect edges again to catch internal features
            // This helps detect features inside white objects (like eyes on white face)
            const inverted = new cv.Mat();
            cv.bitwise_not(blurred, inverted);
            const edgesInverted = new cv.Mat();
            cv.Canny(inverted, edgesInverted, lowThreshold, highThreshold, 3, true);
            
            // Combine both edge detections to get all edges (internal and external)
            cv.bitwise_or(edges, edgesInverted, edges);
            
            inverted.delete();
            edgesInverted.delete();

            // Convert Mat back to ImageData
            // Create a temporary canvas to render the edges
            const outputCanvas = document.createElement('canvas');
            outputCanvas.width = width;
            outputCanvas.height = height;
            
            // Check edge data before rendering
            const edgeDataArray = new Uint8Array(edges.data);
            let edgePixelCount = 0;
            for (let i = 0; i < edgeDataArray.length; i++) {
              if (edgeDataArray[i] > 100) edgePixelCount++;
            }
            console.log(`OpenCV Canny found ${edgePixelCount} edge pixels`);
            
            cv.imshow(outputCanvas, edges);
            
            const outputCtx = outputCanvas.getContext('2d');
            if (!outputCtx) {
              reject(new Error('Failed to get output canvas context'));
              return;
            }
            const output = outputCtx.getImageData(0, 0, width, height);

            // Cleanup
            src.delete();
            gray.delete();
            blurred.delete();
            edges.delete();

            resolve(output);
          } catch (err) {
            reject(err);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for OpenCV processing'));
        };

        img.src = dataUrl;
      } catch (err) {
        reject(err);
      }
    });
  };

  // Convert Canny edge detection results to SVG paths using OpenCV findContours (best method)
  const imageDataToSVG = (imageData: ImageData, simplifyEpsilon: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!opencvReady || !window.cv) {
        reject(new Error('OpenCV.js is not loaded yet'));
        return;
      }

      const cv = window.cv;
      const width = imageData.width;
      const height = imageData.height;

      try {
        // Create a temporary canvas to load the edge data
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) {
          reject(new Error('Failed to create canvas context'));
          return;
        }
        tempCtx.putImageData(imageData, 0, 0);

        // Read the edge image
        const edgeMat = cv.imread(tempCanvas);
        
        // Convert to grayscale for contour detection
        const gray = new cv.Mat();
        const COLOR_RGBA2GRAY = cv.COLOR_RGBA2GRAY !== undefined ? cv.COLOR_RGBA2GRAY : 6;
        cv.cvtColor(edgeMat, gray, COLOR_RGBA2GRAY);

        // Find contours - use RETR_LIST to get ALL contours including internal ones
        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        const RETR_LIST = cv.RETR_LIST !== undefined ? cv.RETR_LIST : 1; // Get all contours, not just external
        const CHAIN_APPROX_SIMPLE = cv.CHAIN_APPROX_SIMPLE !== undefined ? cv.CHAIN_APPROX_SIMPLE : 2;
        
        cv.findContours(gray, contours, hierarchy, RETR_LIST, CHAIN_APPROX_SIMPLE);

        const paths: string[] = [];
        
        // Process each contour
        for (let i = 0; i < contours.size(); i++) {
          const contour = contours.get(i);
          
          // Simplify the contour using approxPolyDP
          const approx = new cv.Mat();
          cv.approxPolyDP(contour, approx, simplifyEpsilon, true);
          
          // Convert to SVG path
          if (approx.rows > 0) {
            const pathPoints: string[] = [];
            for (let j = 0; j < approx.rows; j++) {
              // Access point data correctly for OpenCV.js
              // Contours are stored as Int32Array in data32S
              const pointPtr = approx.intPtr(j);
              const x = pointPtr[0];
              const y = pointPtr[1];
              if (j === 0) {
                pathPoints.push(`M ${x} ${y}`);
              } else {
                pathPoints.push(`L ${x} ${y}`);
              }
            }
            pathPoints.push('Z'); // Close the path
            paths.push(`<path d="${pathPoints.join(' ')}" stroke="#000000" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`);
          }
          
          approx.delete();
        }

        // Cleanup
        edgeMat.delete();
        gray.delete();
        contours.delete();
        hierarchy.delete();

        console.log(`Generated ${paths.length} SVG paths from contours`);

        if (paths.length === 0) {
          resolve(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
<text x="10" y="20" fill="black" font-size="16">No outlines found. Try adjusting Canny thresholds.</text>
</svg>`);
          return;
        }

        resolve(`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="display: block; max-width: 100%; height: auto;">
${paths.join('\n')}
</svg>`);
      } catch (err) {
        console.error('Error converting to SVG:', err);
        reject(err);
      }
    });
  };


  // Process image: convert to B&W, apply sharpness, then to SVG
  const processImage = async (img?: HTMLImageElement) => {
    const imageToProcess = img || originalImage;
    if (!imageToProcess || !canvasRef.current) return;

    if (!opencvReady) {
      setError('OpenCV.js is still loading. Please wait...');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setError(t('pages.freeTools.imageToSVG.errors.canvasContext'));
        setLoading(false);
        return;
      }

      // Set canvas size
      const maxSize = 2000; // Limit size for performance
      let width = imageToProcess.width;
      let height = imageToProcess.height;
      
      if (width > maxSize || height > maxSize) {
        const scale = Math.min(maxSize / width, maxSize / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image to canvas
      ctx.drawImage(imageToProcess, 0, 0, width, height);

      // Get image data
      let imageData = ctx.getImageData(0, 0, width, height);

      // Step 1: Remove background first
      imageData = removeBackground(imageData);
      ctx.putImageData(imageData, 0, 0);

      // Step 2: Convert to grayscale (Canny works on grayscale)
      const grayscale = convertToGrayscale(imageData);

      // Step 3: Apply Canny edge detection with optimal parameters
      console.log(`Canny thresholds: low=${cannyLow}, high=${cannyHigh}, blur=${blurAmount}`);
      
      const edgeData = await cannyEdgeDetection(grayscale, cannyLow, cannyHigh, blurAmount);

      // Convert edge data to SVG using OpenCV findContours (best method)
      const svg = await imageDataToSVG(edgeData, simplifyEpsilon);
      console.log('SVG generated, length:', svg.length);
      setSvgOutput(svg);

      setLoading(false);
    } catch (err) {
      console.error('Error processing image:', err);
      setError(t('pages.freeTools.imageToSVG.errors.processingFailed'));
      setLoading(false);
    }
  };

  // Reprocess when parameters change
  useEffect(() => {
    if (originalImage) {
      processImage();
    }
  }, [cannyLow, cannyHigh, blurAmount, simplifyEpsilon]);

  // Download SVG
  const handleDownloadSVG = () => {
    if (!svgOutput) return;

    // Track download event
    track('Image to SVG - Download SVG', {
      svg_length: svgOutput.length,
      image_width: originalImage?.width,
      image_height: originalImage?.height,
      canny_low: cannyLow,
      canny_high: cannyHigh,
      blur_amount: blurAmount,
      simplify_epsilon: simplifyEpsilon,
    });

    const blob = new Blob([svgOutput], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'converted-image.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download PNG
  const handleDownloadPNG = async () => {
    if (!svgOutput || !originalImage) return;

    // Track download event
    track('Image to SVG - Download PNG', {
      image_width: originalImage.width,
      image_height: originalImage.height,
      canny_low: cannyLow,
      canny_high: cannyHigh,
      blur_amount: blurAmount,
      simplify_epsilon: simplifyEpsilon,
    });

    try {
      // Create a temporary canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size to match original image
      canvas.width = originalImage.width;
      canvas.height = originalImage.height;

      // Create an image from the SVG
      const img = new Image();
      const svgBlob = new Blob([svgOutput], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          // Draw SVG image to canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG image'));
        };
        img.src = url;
      });

      // Convert canvas to PNG blob
      canvas.toBlob((blob) => {
        if (!blob) return;

        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'converted-image.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }, 'image/png');
    } catch (err) {
      console.error('Error downloading PNG:', err);
      setError('Failed to download PNG. Please try again.');
    }
  };

  // Reset
  const handleReset = () => {
    setOriginalImage(null);
    setSvgOutput(null);
    setCannyLow(100);
    setCannyHigh(120);
    setBlurAmount(3.0);
    setSimplifyEpsilon(0.5);
    setError(null);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('pages.freeTools.imageToSVG.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 3,
            textAlign: isRTL ? 'right' : 'left',
            whiteSpace: 'pre-line',
          }}
        >
          {t('pages.freeTools.imageToSVG.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon sx={{ ml: 0.5 }} />}
            sx={{ mb: 2, gap: 1 }}
            disabled={loading}
          >
            {loading ? t('pages.freeTools.imageToSVG.processing') : t('pages.freeTools.imageToSVG.uploadImage')}
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
          {originalImage && (
            <IconButton onClick={handleReset} sx={{ ml: 1 }} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          )}
        </Box>

        {originalImage && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Optimized controls based on best practices - 2 rows of 2 sliders */}
            <Grid container spacing={6}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRTL ? 'right' : 'left' }}>
                    Canny Low Threshold: {cannyLow}
                  </Typography>
                  <Slider
                    value={cannyLow}
                    onChange={(_, newValue) => setCannyLow(newValue as number)}
                    min={10}
                    max={100}
                    step={5}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{
                      '& .MuiSlider-root': {
                        padding: '13px 6px !important',
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.38,
                        width: 'calc(100% - 12px) !important',
                        left: '6px !important',
                      },
                      '& .MuiSlider-track': {
                        opacity: 1,
                        left: '6px !important',
                      },
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRTL ? 'right' : 'left' }}>
                    Canny High Threshold: {cannyHigh} (recommended: {cannyLow * 3})
                  </Typography>
                  <Slider
                    value={cannyHigh}
                    onChange={(_, newValue) => setCannyHigh(newValue as number)}
                    min={50}
                    max={300}
                    step={10}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{
                      '& .MuiSlider-root': {
                        padding: '13px 6px !important',
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.38,
                        width: 'calc(100% - 12px) !important',
                        left: '6px !important',
                      },
                      '& .MuiSlider-track': {
                        opacity: 1,
                        left: '6px !important',
                      },
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRTL ? 'right' : 'left' }}>
                    Blur Amount (Noise Reduction): {blurAmount.toFixed(1)}
                  </Typography>
                  <Slider
                    value={blurAmount}
                    onChange={(_, newValue) => setBlurAmount(newValue as number)}
                    min={0.5}
                    max={3.0}
                    step={0.1}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{
                      '& .MuiSlider-root': {
                        padding: '13px 6px !important',
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.38,
                        width: 'calc(100% - 12px) !important',
                        left: '6px !important',
                      },
                      '& .MuiSlider-track': {
                        opacity: 1,
                        left: '6px !important',
                      },
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, textAlign: isRTL ? 'right' : 'left' }}>
                    Path Simplification: {simplifyEpsilon.toFixed(1)} (higher = simpler)
                  </Typography>
                  <Slider
                    value={simplifyEpsilon}
                    onChange={(_, newValue) => setSimplifyEpsilon(newValue as number)}
                    min={0.5}
                    max={5.0}
                    step={0.1}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{
                      '& .MuiSlider-root': {
                        padding: '13px 6px !important',
                      },
                      '& .MuiSlider-rail': {
                        opacity: 0.38,
                        width: 'calc(100% - 12px) !important',
                        left: '6px !important',
                      },
                      '& .MuiSlider-track': {
                        opacity: 1,
                        left: '6px !important',
                      },
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Original and Output side by side */}
            <Grid container spacing={2}>
              {/* Original Image */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>
                      {t('pages.freeTools.imageToSVG.original')}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={originalImage.src}
                        alt="Original"
                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* SVG Output */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ textAlign: isRTL ? 'right' : 'left', mb: 1 }}>
                      {t('pages.freeTools.imageToSVG.svgOutput')}
                    </Typography>
                    {svgOutput ? (
                      <Box
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          p: 1,
                          backgroundColor: '#fff',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '200px',
                          width: '100%',
                          '& svg': {
                            maxWidth: '100%',
                            height: 'auto',
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: svgOutput }}
                      />
                    ) : (
                      <Box
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          p: 1,
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '200px',
                          width: '100%',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {loading ? t('pages.freeTools.imageToSVG.processing') : 'Processing...'}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Download buttons below images */}
            {svgOutput && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<DownloadIcon sx={{ ml: 0.5 }} />}
                  onClick={handleDownloadSVG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.imageToSVG.downloadSvg')}
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<DownloadIcon sx={{ ml: 0.5 }} />}
                  onClick={handleDownloadPNG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.imageToSVG.downloadPng')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
};

